<script setup>
import { computed } from 'vue'
import { ArrowLeft, RefreshCw, RotateCcw } from '@lucide/vue'
import { Badge, Button } from '@drama/ui'

import AuditId from './AuditId.vue'
import { clockTime, useAuditText, variantOf } from './audit-text.js'
import { asArray, chargedPointsTotal } from './run-observer.js'

const props = defineProps({
  run: { type: Object, required: true },
  snapshot: { type: Object, required: true },
  branch: { type: Object, required: true },
  // 当前分支的回合请求，按开始时间升序。
  requests: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  loadedAt: { type: Date, default: null },
  failedJobs: { type: Array, required: true },
  // 正在重试的任务 id；整批重试时为 all，空串表示没有。
  retrying: { type: String, default: '' },
  // 正在从这一局建模型对比（C27）。
  comparing: { type: Boolean, default: false },
})
const emit = defineEmits(['refresh', 'retry', 'compare'])

const { t, tr } = useAuditText()
const turnCount = computed(() => asArray(props.branch.turns).length)
// 快照只记写作者模式名，实际模型随每次回合请求记录；芯片显示最近一次提交所用的模型。
const latestModel = computed(() => props.requests.findLast((request) => request.status === 'committed' && request.model)?.model ?? null)
const writerTitle = computed(() => tr('bar.writerTitle', { performer: props.snapshot.performer_id || '—', model: latestModel.value || '—' }))
const points = computed(() => chargedPointsTotal(props.snapshot))
</script>

<template>
  <header class="au-bar" data-testid="run-summary">
    <div class="au-bar__lead">
      <RouterLink class="au-bar__back" :to="{ name: 'novel-runs' }"><ArrowLeft :size="14" aria-hidden="true" />{{ tr('back') }}</RouterLink>
      <h1 class="au-bar__title" :title="tr('spoilerNotice')">{{ run.pack_title }}</h1>
      <AuditId :id="run.id" />
    </div>
    <div class="au-bar__chips">
      <Badge size="sm" :variant="variantOf(run.status)">{{ t(`visualGameAdmin.novelRuns.list.statuses.${run.status}`) }}</Badge>
      <Badge size="sm">{{ tr('bar.turns', { count: turnCount }) }}</Badge>
      <Badge size="sm" :variant="run.has_pending_jobs ? 'warning' : 'neutral'">{{ run.has_pending_jobs ? tr('bar.jobsPending') : tr('bar.jobsIdle') }}</Badge>
      <Badge size="sm" variant="info" :title="writerTitle" data-testid="observer-performer-model">{{ tr('bar.writer', { model: latestModel || snapshot.performer_id || '—' }) }}</Badge>
      <Badge size="sm">{{ tr('bar.points', { points }) }}</Badge>
    </div>
    <div class="au-bar__actions">
      <Button v-if="run.purpose === 'play'" variant="secondary" size="sm" :loading="comparing" data-testid="observer-compare" @click="emit('compare')">
        {{ t('visualGameAdmin.novelComparison.open') }}
      </Button>
      <Button variant="secondary" size="sm" :loading="loading" :title="tr('bar.refreshTitle', { time: clockTime(loadedAt) })" @click="emit('refresh')">
        <RefreshCw :size="14" aria-hidden="true" /> {{ tr('refresh') }}
      </Button>
      <Button v-if="failedJobs.length" variant="danger" size="sm" :loading="retrying === 'all'" :disabled="retrying !== ''" @click="emit('retry', failedJobs)">
        <RotateCcw :size="14" aria-hidden="true" /> {{ tr('bar.retryFailed', { count: failedJobs.length }) }}
      </Button>
    </div>
  </header>
</template>
